import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { CheckCircle2, Mail, MapPin, Phone } from 'lucide-react'
import { pb, errorMessage } from '@/lib/pb'
import { useSettings } from '@/lib/queries'
import { BrandMask, Container, MeshTexture } from '@/components/site/primitives'
import { PageHero } from '@/components/site/page-hero'
import { Button } from '@/components/ui/button'
import { Field, Input, Select, Textarea } from '@/components/ui/field'
import { SOCIAL_LINKS } from '@/components/site/social-icons'
import { usePageMeta } from '@/components/layout/site-layout'

const SUBJECTS = [
  { value: 'trials', label: 'I want to trial' },
  { value: 'general', label: 'General enquiry' },
  { value: 'sponsorship', label: 'Sponsorship' },
  { value: 'media', label: 'Media & press' },
  { value: 'other', label: 'Something else' },
] as const

const schema = z.object({
  name: z.string().trim().min(2, 'Please tell us your name'),
  email: z.email('That email address does not look right'),
  phone: z.string().trim().max(60).optional(),
  subject: z.enum(['general', 'trials', 'sponsorship', 'media', 'other']),
  message: z.string().trim().min(10, 'A little more detail, please').max(4000),
  // Honeypot. Real people never see this field; bots fill everything.
  website: z.string().max(0).optional(),
})

type FormValues = z.infer<typeof schema>

export default function ContactPage() {
  usePageMeta('Contact', 'Get in touch with AFC Vikings Cairo — trials, sponsorship and media.')

  const { data: settings } = useSettings()
  const [sent, setSent] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { subject: 'trials', website: '' },
  })

  const onSubmit = async (values: FormValues) => {
    setSubmitError('')
    try {
      await pb.collection('contact_messages').create(values)
      setSent(true)
      reset()
    } catch (error) {
      setSubmitError(
        errorMessage(error, 'We could not send that. Please try again, or email us directly.')
      )
    }
  }

  const socials = SOCIAL_LINKS.map(({ key, label, Icon }) => ({
    label,
    Icon,
    href: settings?.[key] ?? '',
  })).filter((social) => social.href)

  const details = [
    settings?.address
      ? { Icon: MapPin, label: 'Where', value: settings.address, href: settings.map_url }
      : null,
    settings?.contact_email
      ? {
          Icon: Mail,
          label: 'Email',
          value: settings.contact_email,
          href: `mailto:${settings.contact_email}`,
        }
      : null,
    settings?.contact_phone
      ? {
          Icon: Phone,
          label: 'Phone',
          value: settings.contact_phone,
          href: `tel:${settings.contact_phone.replace(/\s/g, '')}`,
        }
      : null,
  ].filter(Boolean) as { Icon: typeof Mail; label: string; value: string; href?: string }[]

  return (
    <>
      <PageHero
        kicker="Say hello"
        rail="Get in touch"
        title="Contact"
        subtitle={
          settings?.training_times
            ? `Trials, sponsorship, press — or just to ask a question. Training: ${settings.training_times}`
            : 'Trials, sponsorship, press — or just to ask a question.'
        }
      />

      <section className="bg-ink relative overflow-hidden py-14 md:py-20">
        <MeshTexture opacity={0.04} />

        <Container className="relative">
          <div className="grid gap-12 lg:grid-cols-[1.25fr_1fr] lg:gap-20">
            <div>
              {sent ? (
                <div className="border-blood/40 bg-blood/5 flex flex-col items-start border p-8">
                  <CheckCircle2 className="text-blood-bright size-8" aria-hidden />
                  <h2 className="condensed text-bone mt-5 text-2xl font-bold tracking-[0.04em]">
                    Message sent
                  </h2>
                  <p className="text-bone-dim mt-3 max-w-md leading-relaxed">
                    Thanks for getting in touch. Someone from the club will come back to you —
                    usually within a couple of days.
                  </p>
                  <Button variant="outline" className="mt-7" onClick={() => setSent(false)}>
                    Send another
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Your name" htmlFor="name" required error={errors.name?.message}>
                      <Input
                        id="name"
                        autoComplete="name"
                        aria-invalid={Boolean(errors.name)}
                        {...register('name')}
                      />
                    </Field>

                    <Field label="Email" htmlFor="email" required error={errors.email?.message}>
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        aria-invalid={Boolean(errors.email)}
                        {...register('email')}
                      />
                    </Field>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field
                      label="Phone"
                      htmlFor="phone"
                      hint="Optional"
                      error={errors.phone?.message}
                    >
                      <Input id="phone" type="tel" autoComplete="tel" {...register('phone')} />
                    </Field>

                    <Field label="What is it about?" htmlFor="subject" required>
                      <Select id="subject" {...register('subject')}>
                        {SUBJECTS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>

                  <Field label="Message" htmlFor="message" required error={errors.message?.message}>
                    <Textarea
                      id="message"
                      rows={6}
                      aria-invalid={Boolean(errors.message)}
                      {...register('message')}
                    />
                  </Field>

                  {/* Honeypot — hidden from people and from screen readers. */}
                  <div className="hidden" aria-hidden>
                    <label htmlFor="website">Website</label>
                    <input
                      id="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      {...register('website')}
                    />
                  </div>

                  {submitError ? (
                    <p className="border-blood/40 bg-blood/10 text-blood-bright border px-4 py-3 text-sm">
                      {submitError}
                    </p>
                  ) : null}

                  <Button type="submit" size="lg" loading={isSubmitting}>
                    Send message
                  </Button>
                </form>
              )}
            </div>

            <aside className="space-y-8">
              <dl className="divide-hairline border-hairline divide-y border-y">
                {details.map((detail) => (
                  <div key={detail.label} className="flex gap-4 py-5">
                    <detail.Icon className="text-blood mt-0.5 size-4 shrink-0" aria-hidden />
                    <div className="min-w-0">
                      <dt className="eyebrow text-bone-faint">{detail.label}</dt>
                      <dd className="text-bone mt-1.5 leading-relaxed">
                        {detail.href ? (
                          <a
                            href={detail.href}
                            target={detail.href.startsWith('http') ? '_blank' : undefined}
                            rel="noreferrer noopener"
                            className="hover:text-blood-bright transition-colors"
                          >
                            {detail.value}
                          </a>
                        ) : (
                          detail.value
                        )}
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>

              {socials.length > 0 ? (
                <div>
                  <p className="eyebrow text-bone-faint mb-4">Follow the club</p>
                  <div className="flex flex-wrap gap-2">
                    {socials.map(({ label, href, Icon }) => (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label={label}
                        className="border-hairline text-bone-dim hover:border-blood hover:text-blood-bright flex size-11 items-center justify-center border transition-colors"
                      >
                        <Icon className="size-4" />
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* The brand map graphic, tinted rather than an embedded iframe —
                  no third-party script, no cookie banner, no CSP exception. */}
              <a
                href={settings?.map_url || undefined}
                target={settings?.map_url ? '_blank' : undefined}
                rel="noreferrer noopener"
                className="group border-hairline bg-ink-sunken relative block aspect-4/3 overflow-hidden border"
              >
                <BrandMask
                  src="map"
                  className="text-blood-deep absolute inset-0 opacity-70 transition-opacity group-hover:opacity-100"
                  style={{ maskSize: 'cover' }}
                />
                <div className="absolute inset-0 flex items-end p-5">
                  <div>
                    <p className="eyebrow text-bone-faint">Home ground</p>
                    <p className="condensed text-bone mt-1 text-lg font-bold tracking-[0.04em]">
                      {settings?.address || 'Cairo, Egypt'}
                    </p>
                  </div>
                </div>
              </a>
            </aside>
          </div>
        </Container>
      </section>
    </>
  )
}
